import { MIN_GAP } from '../../engine/caves/gap';
import { CAVE_HINTS } from '../hollows/caves';
import {
  type BiomeId,
  type BiomeSlot,
  biomeSlot,
  defaultWorld,
  type PlanetTypeId,
  type SkyId,
  type SpeciesId,
  skyDefaults,
  typeDefaults,
  type UniverseSpec,
  type WhereId,
  type WorldSpec,
} from './spec';

/*
 * Prefab universes: ready-made starting points for the editor, two or three small worlds of
 * several biomes each (none over 10 km across), joined by caves. They are plain specs, so they
 * load, change, save and share like anything made by hand. Nexus, the game's default world,
 * is one too: a tiny hub with the shortest caves to five worlds.
 */

export const PREFAB_IDS = ['nexus', 'seasons', 'oasis', 'crossroads', 'glowdeep', 'tides', 'moons'] as const;
export type PrefabId = (typeof PREFAB_IDS)[number];

export interface Prefab {
  label: string;
  /** A line about it, shown once it's loaded. */
  about: string;
  make: () => UniverseSpec;
  /** First-visit hints when it's played as a scene of its own (`?scene=<id>`). */
  hints?: string[];
}

/** A biome slot: its share, where it leans and (if not its own) its two tree species. */
function slot(
  id: BiomeId,
  share: number,
  where: WhereId = 'anywhere',
  trees?: [SpeciesId, SpeciesId],
): BiomeSlot {
  const s = biomeSlot(id, share, where);
  if (trees) [s.treeA, s.treeB] = trees;
  return s;
}

/** A world of this planet type, sky and biomes, then any other settings. */
function world(
  name: string,
  radius: number,
  seed: number,
  type: PlanetTypeId,
  sky: SkyId,
  biomes: BiomeSlot[],
  set: Partial<WorldSpec> = {},
): WorldSpec {
  return {
    ...defaultWorld('verdant', radius, seed, name),
    ...typeDefaults(type),
    ...skyDefaults(sky, radius),
    biomes,
    ...set,
  };
}

/** A universe of these worlds, starting in the first, joined by caves [a, b, length (0: automatic)]. */
function universe(
  name: string,
  seed: number,
  worlds: WorldSpec[],
  caves: [number, number, number][],
): UniverseSpec {
  return {
    version: 2,
    name,
    seed,
    start: 0,
    worlds,
    caves: caves.map(([a, b, length]) => ({ a, b, length })),
  };
}

/** Radius (m) of Nexus's hub and its four tiny worlds: the smallest a world can be. */
export const NEXUS_TINY = 200;
/** Radius (m) of Nexus's one big world: 20 km across. */
export const NEXUS_BIG = 10_000;

export const PREFABS: Record<PrefabId, Prefab> = {
  nexus: {
    label: 'Nexus: a tiny hub with short caves to five worlds',
    about:
      'A meadow world 400 m across, with the shortest caves to four more as small (dunes, snow, a glowing marsh, lava) and a sunlit world 20 km across.',
    hints: [
      'Nexus is a tiny world, 400 m across. Look up: the sky is its far side.',
      'Each ring of stones is a cave mouth. Walk in: five short caves lead to five more worlds.',
      ...CAVE_HINTS.slice(1),
      'Hold T to watch a day pass. V to fly.',
    ],
    make: () =>
      universe(
        'Nexus',
        // (A seed whose placement fits every cave at the shortest length.)
        1337,
        [
          world('Nexus', NEXUS_TINY, 1301, 'continents', 'sunlit', [slot('meadow', 1)]),
          // The big world's cave is placed first: it fills most of the sky round the hub, and
          // the tiny worlds then find room on the other side, each at the shortest cave.
          world('Verdance', NEXUS_BIG, 1302, 'continents', 'sunlit', [slot('meadow', 1)]),
          world('Sandglass', NEXUS_TINY, 1303, 'plains', 'golden', [
            slot('desert', 0.75),
            slot('badlands', 0.25, 'high'),
          ]),
          world('Hoarfrost', NEXUS_TINY, 1304, 'highlands', 'pale', [
            slot('snowfield', 0.6, 'high'),
            slot('tundra', 0.4, 'low'),
          ]),
          world('Lumen', NEXUS_TINY, 1305, 'marsh', 'glowing', [slot('glowmarsh', 1)]),
          world('Cinderfall', NEXUS_TINY, 1306, 'volcanic', 'ember', [
            slot('lava', 0.6, 'low'),
            slot('ashlands', 0.4, 'high'),
          ]),
        ],
        [
          [0, 1, MIN_GAP],
          [0, 2, MIN_GAP],
          [0, 3, MIN_GAP],
          [0, 4, MIN_GAP],
          [0, 5, MIN_GAP],
        ],
      ),
  },
  seasons: {
    label: 'The turning year: summer, autumn, winter',
    about: 'Three worlds in a row, each a season: green summer, an autumn afternoon, then snow.',
    make: () =>
      universe(
        'The turning year',
        4100,
        [
          world('Summerholm', 4200, 4101, 'continents', 'sunlit', [
            slot('meadow', 0.55),
            slot('jungle', 0.2, 'low', ['oak', 'palm']),
            slot('heath', 0.25, 'high'),
          ]),
          world(
            'Emberleaf',
            3400,
            4102,
            'continents',
            'sunlit',
            [
              slot('autumn', 0.55),
              slot('heath', 0.25, 'high'),
              slot('meadow', 0.2, 'low', ['oak', 'goldbirch']),
            ],
            { sunColor: '#ffd29a', duskColor: '#ff7a3a', birds: false },
          ),
          world('Rimeholt', 2800, 4103, 'highlands', 'pale', [
            slot('taiga', 0.45, 'low'),
            slot('tundra', 0.25, 'poles'),
            slot('snowfield', 0.3, 'high'),
          ]),
        ],
        [
          [0, 1, 0],
          [1, 2, 0],
        ],
      ),
  },
  oasis: {
    label: 'Oasis road: dunes, then palm islands',
    about: 'A wide desert of dunes and red badlands, with a short cave to green islands.',
    make: () =>
      universe(
        'Oasis road',
        5200,
        [
          world('Sunscar', 4800, 5201, 'plains', 'golden', [
            slot('desert', 0.55),
            slot('badlands', 0.25, 'high'),
            slot('savanna', 0.2, 'low'),
          ]),
          world(
            'Palmreach',
            2600,
            5202,
            'archipelago',
            'sunlit',
            [slot('jungle', 0.7), slot('savanna', 0.3, 'high', ['acacia', 'palm'])],
            { water: -0.35 },
          ),
        ],
        [[0, 1, 160]],
      ),
  },
  crossroads: {
    label: 'Fire and frost: a green crossroads between lava and ice',
    about: 'Start on a small green world, with a cave down to ash and lava and another up to ice.',
    make: () =>
      universe(
        'Fire and frost',
        6300,
        [
          world('Midmarch', 2400, 6301, 'continents', 'sunlit', [
            slot('heath', 0.45),
            slot('meadow', 0.3, 'low'),
            slot('taiga', 0.25, 'poles'),
          ]),
          world('Ashgate', 3600, 6302, 'volcanic', 'ember', [
            slot('ashlands', 0.5, 'high'),
            slot('lava', 0.35, 'low'),
            slot('badlands', 0.15, 'anywhere', ['charred', 'deadtree']),
          ]),
          world('Frostmaw', 4000, 6303, 'highlands', 'pale', [
            slot('snowfield', 0.5, 'high'),
            slot('tundra', 0.3, 'low'),
            slot('taiga', 0.2, 'low'),
          ]),
        ],
        [
          [0, 1, 0],
          [0, 2, 0],
        ],
      ),
  },
  glowdeep: {
    label: 'Glowdeep: three sunless worlds of living light',
    about: 'No sun anywhere: a glowing marsh, a dark crystal hollow, then a violet spore world.',
    make: () =>
      universe(
        'Glowdeep',
        7400,
        [
          world('Mirelight', 3000, 7401, 'marsh', 'glowing', [
            slot('glowmarsh', 0.6, 'low'),
            slot('alien', 0.25, 'high'),
            slot('crystal', 0.15, 'high'),
          ]),
          world('Shardhollow', 1500, 7402, 'cavern', 'dark', [
            slot('crystal', 0.55),
            slot('darkstone', 0.45, 'low'),
          ]),
          world(
            'Sporewell',
            2200,
            7403,
            'shattered',
            'glowing',
            // (Not the marsh's glowing mushrooms again: where the two meet, their light would double.)
            [
              slot('alien', 0.5, 'anywhere', ['crystal', 'bone']),
              slot('glowmarsh', 0.3, 'low'),
              slot('darkstone', 0.2, 'high'),
            ],
            { glowColor: '#c98bff', exposure: 0.45 },
          ),
        ],
        [
          [0, 1, 120],
          [1, 2, 120],
        ],
      ),
  },
  tides: {
    label: 'Strange tides: an alien sea and its crystal moon',
    about: 'A violet-skied ocean of strange islands, and a small world of crystal spires beside it.',
    make: () =>
      universe(
        'Strange tides',
        8500,
        [
          world(
            'Tealmere',
            4600,
            8501,
            'ocean',
            'alien',
            [
              slot('alien', 0.45),
              slot('jungle', 0.45, 'anywhere', ['mushroom', 'palm']),
              slot('crystal', 0.1, 'high'),
            ],
            { water: -0.6 },
          ),
          world('Violet Spire', 2000, 8502, 'shattered', 'alien', [
            slot('crystal', 0.45, 'high'),
            slot('alien', 0.35),
            slot('ashlands', 0.2, 'low'),
          ]),
        ],
        [[0, 1, 0]],
      ),
  },
  moons: {
    label: 'Pebble moons: three tiny worlds a short hop apart',
    about: 'Three little worlds, each a couple of kilometres across, round one another by short caves.',
    make: () =>
      universe(
        'Pebble moons',
        9600,
        [
          world('Pebblehome', 1400, 9601, 'continents', 'sunlit', [
            slot('meadow', 0.5),
            slot('autumn', 0.3, 'high'),
            slot('heath', 0.2, 'poles'),
          ]),
          world(
            'Duneling',
            1000,
            9602,
            'plains',
            'golden',
            [slot('desert', 0.6), slot('savanna', 0.4, 'low')],
            {
              relief: 1.6,
            },
          ),
          world('Frostbead', 1100, 9603, 'highlands', 'pale', [
            slot('snowfield', 0.5, 'high'),
            slot('taiga', 0.5, 'low'),
          ]),
        ],
        [
          [0, 1, 100],
          [0, 2, 100],
        ],
      ),
  },
};
